import React, {useCallback, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {runInAction} from 'mobx'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {Form, Grid} from 'antd'
import {ModalContainer} from '@/components/containers'
import {
    LinkDetailsModalFooter,
    LinkDetailsModalView,
    LinkShare,
    DownloadPdf,
    TPaymentLinkDetailsModalStore,
    LinkShareFooter,
    DownloadPdfFooter
} from '@/pages/PayByLink/components'
import {PayByLinkPaymentLinkDetailsStoreSymbol} from '@/pages/PayByLink'
import {LinkDetailsModalProps} from './props'

import styles from './styles.scss'

export const PaymentLinkDetailsModal: React.FC<LinkDetailsModalProps> = observer(
    ({injectableIdentifier}: LinkDetailsModalProps) => {
        const {t} = useTranslation()
        const store = useInjection<TPaymentLinkDetailsModalStore>(
            PayByLinkPaymentLinkDetailsStoreSymbol
        )
        const {paymentLink, isMultiUse} = store
        const screens = Grid.useBreakpoint()
        const isMobile = screens.xs
        const [formSms] = Form.useForm()
        const [formEmail] = Form.useForm()
        const [formDownloadQR] = Form.useForm()

        useEffect(() => {
            if (isMultiUse && paymentLink?.terminalId) {
                store.getTerminalSettings(paymentLink.terminalId)
            }
        }, [isMultiUse, paymentLink?.terminalId])

        const getTitle = () => {
            switch (store.widgetType) {
                case 'linkDetails':
                    return store.isNewLinkDetails
                        ? t('PaymentLinkDetailsModal.newPaymentLinkIsReady')
                        : t('PaymentLinkDetailsModal.linkDetails')
                case 'shareViaEmail':
                    return t('PaymentLinkDetailsModal.shareViaEmail')
                case 'shareViaSMS':
                    return t('PaymentLinkDetailsModal.shareViaSMS')
                case 'downloadPdf':
                    return t('PaymentLinkDetailsModal.downloadPdf')
            }
        }

        const handleCloseModal = () => {
            runInAction(() => (store.widgetType = 'linkDetails'))
        }

        const onCancel = () => {
            formDownloadQR.resetFields()
        }

        const onSenderNameChange = useCallback(
            (name) => {
                const {paymentLink} = store

                store.setPaymentLink({...paymentLink, senderName: name})
            },
            [store]
        )

        const renderBody = () => {
            switch (store.widgetType) {
                case 'linkDetails':
                    return (
                        <LinkDetailsModalView
                            createdPaymentLink={store.paymentLink}
                            onSmsBtnClick={() => {
                                store.widgetType = 'shareViaSMS'
                            }}
                            onEmailBtnClick={() => {
                                store.widgetType = 'shareViaEmail'
                            }}
                            onDownloadPdfBtnClick={() => {
                                store.widgetType = 'downloadPdf'
                            }}
                        />
                    )
                case 'shareViaSMS':
                    return (
                        <LinkShare
                            senderNameHandler={onSenderNameChange}
                            paymentLink={store.paymentLink}
                            form={formSms}
                            formId={'linkShareSms'}
                            type={'sms'}
                            isMultiUse={store.isMultiUse}
                            onBackToLinkClick={() => {
                                runInAction(() => {
                                    store.widgetType = 'linkDetails'
                                    store.setPaymentLink({...store.paymentLink, senderName: null})
                                })
                            }}
                        />
                    )
                case 'shareViaEmail':
                    return (
                        <LinkShare
                            senderNameHandler={onSenderNameChange}
                            paymentLink={store.paymentLink}
                            form={formEmail}
                            formId={'linkShareEmail'}
                            type={'email'}
                            isMultiUse={store.isMultiUse}
                            onBackToLinkClick={() => {
                                runInAction(() => {
                                    store.widgetType = 'linkDetails'
                                    store.setPaymentLink({...store.paymentLink, senderName: null})
                                })
                            }}
                        />
                    )

                case 'downloadPdf':
                    return (
                        <DownloadPdf
                            terminalSettings={store.terminalSettings}
                            paymentLink={store.paymentLink}
                            form={formDownloadQR}
                            formId={'downloadPdf'}
                            onDownloadButtonClick={store.getPaymentLinkPdf}
                            onBackToLinkClick={() => {
                                runInAction(() => {
                                    store.widgetType = 'linkDetails'
                                })

                                formDownloadQR.resetFields()
                            }}
                        />
                    )
            }
        }

        const renderFooter = () => {
            switch (store.widgetType) {
                case 'linkDetails':
                    return isMobile ? null : <LinkDetailsModalFooter store={store} />
                case 'shareViaSMS':
                    return (
                        <LinkShareFooter
                            form={formSms}
                            formId={'linkShareSms'}
                            type={'sms'}
                            closeModal={handleCloseModal}
                        />
                    )
                case 'shareViaEmail':
                    return (
                        <LinkShareFooter
                            form={formEmail}
                            formId={'linkShareEmail'}
                            type={'email'}
                            closeModal={handleCloseModal}
                        />
                    )
                case 'downloadPdf':
                    return (
                        <DownloadPdfFooter
                            form={formEmail}
                            formId={'downloadPdf'}
                            closeModal={handleCloseModal}
                        />
                    )
            }
        }

        return (
            <ModalContainer
                className={styles.modal}
                title={!isMobile && getTitle()}
                injectableIdentifier={injectableIdentifier}
                width={'900px'}
                destroyOnClose
                footer={renderFooter()}
                onCancel={onCancel}
            >
                {renderBody()}
            </ModalContainer>
        )
    }
)
